import { useEffect, useRef, useState, type JSX, type PointerEvent } from 'react';
import { validateBrowserSettings, type BrowserSettings } from '../../../sources/browser';
import { Dialog } from '../../components/Dialog';
import { ViewCanvas } from '../../components/ViewCanvas';
import { useRuntime, useStudio } from '../../context';
import { safely } from '../../safe';
export function BrowserInteractionDialog({
  sourceId,
  width,
  height,
  url,
  onClose,
  onDetachedChange,
}: {
  sourceId: string;
  width: number;
  height: number;
  url: string;
  onClose(): void;
  onDetachedChange?(detached:boolean):void;
}): JSX.Element {
  const studio = useStudio();
  const currentAddress = useRuntime((r) => r.sources[sourceId]?.live?.address);
  const [address, setAddress] = useState(url),
    [error, setError] = useState('');
  const [popup,setPopup]=useState<Window|null>(null);
  const pressedKeys=useRef(new Map<string,number>());
  const clicks=useRef({at:0,x:0,y:0,button:0,count:0});
  const modifiers=(e:{shiftKey:boolean;ctrlKey:boolean;altKey:boolean;metaKey:boolean;buttons?:number}):number=>
    (e.shiftKey?2:0)|(e.ctrlKey?4:0)|(e.altKey?8:0)|(e.metaKey?128:0)|((e.buttons??0)&1?16:0)|((e.buttons??0)&4?32:0)|((e.buttons??0)&2?64:0);
  const keyCode=(key:string,code:string,legacy:number):number=>{
    const named:Record<string,number>={Backspace:8,Tab:9,Enter:13,Shift:16,Control:17,Alt:18,Escape:27,' ':32,PageUp:33,PageDown:34,End:35,Home:36,ArrowLeft:37,ArrowUp:38,ArrowRight:39,ArrowDown:40,Delete:46,Meta:91};
    return named[key]??(/^Key[A-Z]$/.test(code)?code.charCodeAt(3):/^Digit[0-9]$/.test(code)?code.charCodeAt(5):legacy);
  };
  useEffect(()=>{
    onDetachedChange?.(popup!==null);
    return ()=>onDetachedChange?.(false);
  },[popup,onDetachedChange]);
  useEffect(()=>{
    if(!popup)return;
    const closed=()=>setPopup(null);
    popup.addEventListener('pagehide',closed);
    const timer=setInterval(()=>{if(popup.closed)setPopup(null);},500);
    return ()=>{clearInterval(timer);popup.removeEventListener('pagehide',closed);popup.close();};
  },[popup]);
  const detach=():void=>{
    const next=window.open('about:blank','','popup,width=1000,height=740,resizable=yes,scrollbars=no');
    if(!next){setError('Allow popups for Studio to open interaction in a separate window.');return;}
    next.document.title='Browser interaction — OhMyStudio';
    next.document.documentElement.className=document.documentElement.className;
    next.document.documentElement.lang=document.documentElement.lang;
    next.document.documentElement.dataset.theme=document.documentElement.dataset.theme??'dark';
    next.document.documentElement.style.cssText=document.documentElement.style.cssText;
    next.document.body.className=document.body.className;
    next.document.body.style.cssText='margin:0;overflow:hidden';
    for(const style of document.querySelectorAll('link[rel="stylesheet"],style'))next.document.head.appendChild(style.cloneNode(true));
    setError('');setPopup(next);next.focus();
  };
  useEffect(() => setAddress(currentAddress ?? url), [currentAddress, url]);
  const navigate = (): void => {
    try {
      const source = studio.project.getState().project.sources[sourceId];
      if (!source) return;
      const next = validateBrowserSettings({ ...source.settings, url: address } as BrowserSettings);
      studio.commands.updateSourceSettings(sourceId, { url: next.url });
      setError('');
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const history = (action: string): void => {
    void safely(studio, () => studio.invokeSource(sourceId, action));
  };
  const input = (event: Record<string, unknown>): void => {
    void safely(studio, () => studio.invokeSource(sourceId, 'browser-input', event));
  };
  const point = (event: PointerEvent<HTMLDivElement>): { x: number; y: number } => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(width - 1, Math.floor(((event.clientX - rect.left) * width) / rect.width))),
      y: Math.max(0, Math.min(height - 1, Math.floor(((event.clientY - rect.top) * height) / rect.height))),
    };
  };
  return (
    <Dialog
      title="Interact with Browser"
      width={800}
      height={640}
      resizable
      movable
      flush
      ownerDocument={popup?.document}
      standalone={popup!==null}
      onClose={onClose}
      footer={
        <>
          <button className="ui-btn" onClick={popup?()=>setPopup(null):detach}>{popup?'Return to Studio':'Open in window'}</button>
          <button className="ui-btn" onClick={onClose}>Close</button>
        </>
      }
    >
      <div style={{display:'flex',flexDirection:'column',gap:10,padding:14,width:'100%',minWidth:0,minHeight:0}}>
      <form
        className="ui-row"
        onSubmit={(event) => {
          event.preventDefault();
          navigate();
        }}
      >
        <button type="button" className="ui-btn" onClick={() => history('back')}>
          Back
        </button>
        <button type="button" className="ui-btn" onClick={() => history('forward')}>
          Forward
        </button>
        <button type="button" className="ui-btn" onClick={() => history('reload')}>
          Reload
        </button>
        <input
          aria-label="Browser address"
          className="ui-grow"
          style={{minWidth:0}}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
        />
        <button type="submit" className="ui-btn">
          Go
        </button>
      </form>
      {error ? (
        <p role="alert" className="ui-error-text">
          {error}
        </p>
      ) : null}
      <p className="ui-dim">
        Click the preview to focus it. Mouse, scrolling and keyboard input are sent to the native page.
      </p>
      <div style={{flex:'1 1 auto',minHeight:0,containerType:'size',display:'grid',placeItems:'center',overflow:'hidden'}}>
      <div
        role="application"
        aria-label="Browser page interaction"
        tabIndex={0}
        style={{
          width:`min(100cqw, calc(100cqh * ${width/height}))`,aspectRatio:`${width}/${height}`,
          position: 'relative',
          overflow: 'hidden',
          outline: '1px solid var(--ui-border)',
        }}
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.focus();
          e.currentTarget.setPointerCapture(e.pointerId);
          const p=point(e),last=clicks.current;
          const count=e.timeStamp-last.at<500&&Math.abs(e.clientX-last.x)<5&&Math.abs(e.clientY-last.y)<5&&last.button===e.button?last.count%3+1:1;
          clicks.current={x:e.clientX,y:e.clientY,at:e.timeStamp,button:e.button,count};
          input({ kind: 'click', ...p, button: e.button === 2 ? 2 : e.button === 1 ? 1 : 0, up: 0,modifiers:modifiers(e),click_count:count });
        }}
        onPointerUp={(e) => {
          input({ kind: 'click', ...point(e), button: e.button === 2 ? 2 : e.button === 1 ? 1 : 0, up: 1,modifiers:modifiers(e),click_count:clicks.current.count });
          if(e.currentTarget.hasPointerCapture(e.pointerId))e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => input({ kind: 'move', ...point(e),modifiers:modifiers(e) })}
        onWheel={(e) => {
          e.preventDefault();
          const rect = e.currentTarget.getBoundingClientRect();
          input({
            kind: 'scroll',
            x: Math.max(0, Math.min(width - 1, Math.floor(((e.clientX - rect.left) * width) / rect.width))),
            y: Math.max(0, Math.min(height - 1, Math.floor(((e.clientY - rect.top) * height) / rect.height))),
            delta_x: -Math.max(-10000, Math.min(10000, Math.round(e.deltaX))),
            delta_y: -Math.max(-10000, Math.min(10000, Math.round(e.deltaY))),
            modifiers:modifiers(e),
          });
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if(e.nativeEvent.isComposing||e.key==='Process'||e.key==='Dead')return;
          e.preventDefault();
          const code=keyCode(e.key,e.code,e.keyCode);
          if(code>0){pressedKeys.current.set(e.code||e.key,code);input({ kind: 'key', key_code: code, up: 0,modifiers:modifiers(e),...(e.key.length===1?{text:e.key}:{}) });}
          if (e.key.length === 1 && (!e.ctrlKey||e.getModifierState('AltGraph')) && !e.metaKey) input({ kind: 'text', text: e.key, ...(code>0?{key_code:code,modifiers:modifiers(e)}:{}) });
        }}
        onKeyUp={(e) => {
          e.stopPropagation();
          const code=pressedKeys.current.get(e.code||e.key);
          if (code!==undefined) {
            e.preventDefault();
            pressedKeys.current.delete(e.code||e.key);
            input({ kind: 'key', key_code: code, up: 1,modifiers:modifiers(e) });
          }
        }}
        onBlur={()=>{for(const code of pressedKeys.current.values())input({kind:'key',key_code:code,up:1,modifiers:0});pressedKeys.current.clear();}}
        onCompositionEnd={(e) => {
          if (e.data) input({ kind: 'text', text: e.data.slice(0, 1024) });
        }}
        onPaste={(e) => {
          e.preventDefault();
          input({ kind: 'text', text: e.clipboardData.getData('text/plain').slice(0, 1024) });
        }}
      >
        <ViewCanvas
          target={{ type: 'source', sourceId }}
          label="Native Browser interaction preview"
          style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
        />
      </div>
      </div>
      </div>
    </Dialog>
  );
}
